import { useId, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../../lib/api-client";
import { createFamily } from "../../lib/api-family";
import { Icon } from "../../components/Icon";

interface CreateFamilyFormProps {
  /**
   * When true, the component starts on the "no family" empty state.
   * The user reveals the create form by pressing "Создать семью".
   * Defaults to true.
   */
  showEmptyState?: boolean;
}

const GENERIC_ERROR =
  "Не удалось создать семью. Попробуйте ещё раз.";
const ALREADY_IN_FAMILY_ERROR = "Вы уже состоите в семье";

export function CreateFamilyForm({
  showEmptyState = true,
}: CreateFamilyFormProps) {
  const navigate = useNavigate();
  const inputId = useId();
  const errorId = useId();
  const helperId = useId();

  const [showForm, setShowForm] = useState(!showEmptyState);
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !isSubmitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const family = await createFamily(trimmedName);
      navigate(`/families/${family.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ALREADY_IN_FAMILY") {
        setError(ALREADY_IN_FAMILY_ERROR);
      } else {
        setError(GENERIC_ERROR);
      }
      setIsSubmitting(false);
    }
  }

  if (!showForm) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col bg-canvas px-6 md:min-h-full lg:justify-center lg:py-10">
        <header className="py-5 text-center">
          <h1 className="text-lg font-extrabold tracking-[-0.03em] text-content">Семья</h1>
        </header>

        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <div
            aria-hidden="true"
            className="mb-7 flex h-24 w-24 items-center justify-center rounded-[30px] bg-content text-action shadow-[0_18px_40px_rgba(23,23,20,0.18)]"
          >
            <Icon name="family" size={42} />
          </div>
          <h2 className="mb-2 text-[28px] font-extrabold tracking-[-0.05em] text-[#171714]">
            У вас пока нет семьи
          </h2>
          <p className="max-w-[280px] text-sm leading-6 text-muted">
            Создайте семью, чтобы координировать подарки вместе
          </p>
        </div>

        <div className="py-6">
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="h-[52px] w-full cursor-pointer rounded-2xl bg-action text-base font-bold text-white shadow-[0_10px_24px_rgba(194,65,12,0.24)] transition-transform hover:-translate-y-0.5"
          >
            Создать семью
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col bg-canvas md:min-h-full lg:py-8">
      <header className="relative flex items-center justify-center px-4 py-5">
        <button
          type="button"
          onClick={() => {
            if (showEmptyState) {
              setShowForm(false);
              setError(null);
            } else {
              navigate(-1);
            }
          }}
          aria-label="Назад"
          className="absolute left-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white text-content shadow-sm transition-transform hover:-translate-x-0.5"
        >
          <Icon name="arrow-left" size={20} />
        </button>
        <h1 className="text-lg font-extrabold tracking-[-0.03em] text-content">Создать семью</h1>
      </header>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="mx-4 flex flex-1 flex-col rounded-t-[28px] bg-white px-5 shadow-[0_-8px_30px_rgba(35,33,25,0.04)] lg:flex-none lg:rounded-[28px] lg:px-8 lg:py-4 lg:shadow-[0_16px_42px_rgba(35,33,25,0.07)]"
      >
        <div className="pt-2">
          <label
            htmlFor={inputId}
            className="mb-2 block text-sm font-bold text-content"
          >
            Название семьи
          </label>
          <input
            id={inputId}
            type="text"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (error) setError(null);
            }}
            maxLength={255}
            placeholder="Например, Семья Ивановых"
            autoComplete="off"
            aria-invalid={error !== null}
            aria-describedby={error ? `${errorId} ${helperId}` : helperId}
            className="form-control h-[52px] w-full rounded-2xl border border-line bg-white px-4 focus:outline-none"
          />

          {error && (
            <div
              id={errorId}
              role="alert"
              className="mt-3 rounded-[10px] border-l-4 border-danger bg-danger-soft px-4 py-3 text-sm font-medium text-danger"
            >
              {error}
            </div>
          )}

          <p id={helperId} className="mt-2 text-sm text-muted">
            Участники смогут видеть это название
          </p>
        </div>

        <div className="mt-auto py-6">
          <button
            type="submit"
            disabled={!canSubmit}
            className={`h-[50px] w-full rounded-xl text-base font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFBF5] ${
              canSubmit
                ? "cursor-pointer bg-action shadow-[0_10px_24px_rgba(194,65,12,0.24)] hover:bg-action-hover"
                : "cursor-not-allowed bg-surface-disabled"
            }`}
          >
            {isSubmitting ? "Создаём…" : "Создать"}
          </button>
        </div>
      </form>
    </div>
  );
}
